import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { PageHeader, Loading, StatusBadge, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Building2, Plus, Pencil, Archive } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const EMPTY = { name: "", code: "", head: "", description: "", status: "Active" };

export default function Departments() {
  const qc = useQueryClient();
  const { isSuperAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["departments"],
    queryFn: async () => (await api.get("/departments")).data,
  });

  const save = useMutation({
    mutationFn: async () => editId
      ? (await api.put(`/departments/${editId}`, form)).data
      : (await api.post("/departments", form)).data,
    onSuccess: () => {
      toast.success(editId ? "Department updated" : "Department created");
      qc.invalidateQueries({ queryKey: ["departments"] });
      setOpen(false);
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const archive = useMutation({
    mutationFn: async (id) => (await api.delete(`/departments/${id}`)).data,
    onSuccess: () => { toast.success("Department archived"); qc.invalidateQueries({ queryKey: ["departments"] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (d) => { setForm({ name: d.name, code: d.code, head: d.head || "", description: d.description || "", status: d.status }); setEditId(d.id); setOpen(true); };

  return (
    <div>
      <PageHeader overline="Master Data" title="Departments" icon={Building2}
        subtitle="Configurable organisational units. Never hardcoded."
        actions={<Button onClick={openNew} className="bg-navy hover:bg-navy-700 btn-lift" data-testid="add-department-btn"><Plus className="h-4 w-4 mr-1" /> Add Department</Button>}
      />
      {isLoading ? <Loading /> : (data || []).length === 0 ? (
        <EmptyState icon={Building2} title="No departments yet" hint="Create departments like Microbiology, Biochemistry, Phlebotomy."
          action={<Button onClick={openNew} className="bg-navy"><Plus className="h-4 w-4 mr-1" /> Add Department</Button>} />
      ) : (
        <Card className="border-slate-200 shadow-none overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="overline text-slate-500">Code</TableHead>
                <TableHead className="overline text-slate-500">Name</TableHead>
                <TableHead className="overline text-slate-500">Head</TableHead>
                <TableHead className="overline text-slate-500">Status</TableHead>
                <TableHead className="text-right overline text-slate-500">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((d) => (
                <TableRow key={d.id} className="table-row-i" data-testid={`dept-row-${d.code}`}>
                  <TableCell className="font-mono-i text-sm text-teal">{d.code}</TableCell>
                  <TableCell className="font-medium text-navy">{d.name}</TableCell>
                  <TableCell className="text-slate-600">{d.head || "—"}</TableCell>
                  <TableCell><StatusBadge status={d.status} /></TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(d)} data-testid={`edit-dept-${d.code}`}><Pencil className="h-4 w-4" /></Button>
                    {isSuperAdmin && d.status !== "Archived" && (
                      <Button size="sm" variant="ghost" className="text-red-500" onClick={() => archive.mutate(d.id)} data-testid={`archive-dept-${d.code}`}><Archive className="h-4 w-4" /></Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white" data-testid="department-dialog">
          <DialogHeader><DialogTitle className="font-serif-display text-2xl text-navy">{editId ? "Edit" : "New"} Department</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="dept-name" className="mt-1 focus-visible:ring-teal" /></div>
              <div><Label>Code *</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="dept-code" className="mt-1 focus-visible:ring-teal" /></div>
            </div>
            <div><Label>Department Head</Label><Input value={form.head} onChange={(e) => setForm({ ...form, head: e.target.value })} className="mt-1 focus-visible:ring-teal" /></div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 focus-visible:ring-teal" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-navy hover:bg-navy-700" disabled={!form.name || !form.code || save.isPending} onClick={() => save.mutate()} data-testid="save-department">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
